import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { LooseObject } from 'Angular/interfaces/loose-object.interface';

import {
    formFlattenedObject,
    formFlattenedParamString,
    parseUrlParamStringToArray,
    translateArrayToUrlParamString,
} from 'Angular/utils/urls';

@Injectable({
    providedIn: 'root',
})
export class UtilsService {
    constructor(private domSanitizer: DomSanitizer) {}

    copyToClipboard(stringToCopy: string, callback?: () => void): boolean {
        let success: boolean;
        const textArea: HTMLTextAreaElement = document.createElement('textarea');

        // Place in top-left corner
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        // Give small width/height, 1em/1px doesn't work on some browsers
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        // Don't need padding/borders
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        // Set the value of the state_data
        textArea.value = stringToCopy;
        // Add to document body
        document.body.appendChild(textArea);
        // Select and copy text
        textArea.select();
        try {
            if (navigator.clipboard) {
                navigator.clipboard.writeText(stringToCopy).then(() => {
                    if (callback) {
                        callback();
                    }
                });
            } else {
                document.execCommand('copy');
                if (callback) {
                    callback();
                }
            }
            success = true;
        } catch (err) {
            success = false;
        }
        // Clean up
        document.body.removeChild(textArea);

        return success;
    }

    copyStateDataToClipboard(stateData: LooseObject, stateKeys: string[], defaultData: LooseObject): boolean {
        // Get the state data with defaults.
        const copiedData: LooseObject = {};

        for (let i = 0; i < stateKeys.length; i += 1) {
            const key: string = stateKeys[i];
            copiedData[key] = key in stateData ? stateData[key] : defaultData[key] || '';
        }

        return this.copyToClipboard(JSON.stringify(copiedData));
    }

    url = {
        translateArrayToUrlParamString,
        parseUrlParamStringToArray,
        formFlattenedObject,
        formFlattenedParamString,
    };

    addUrlParams(urlTemplate: string, urlParams?: LooseObject): string {
        /**
         * Given a URL template and its url params, return the formatted URL.
         *
         * Example: addUrlParams('/custom_securities/{{ customSecurityId }}', { customSecurityId: 15 });
         * Returns: '/custom_securities/15'
         */
        if (!urlParams) {
            return urlTemplate;
        }

        let formattedString = urlTemplate;
        Object.keys(urlParams).forEach((key: string) => {
            const interpolationString = `{{ ${key} }}`;
            if (urlTemplate.includes(interpolationString)) {
                formattedString = formattedString.replace(interpolationString, urlParams[key]);
            }
        });
        return formattedString;
    }

    addQueryParams(urlTemplate: string, queryParams?: LooseObject): string {
        /**
         * Given a URL template and its query params, return the formatted URL.
         *
         * Example: addQueryParams('/custom_securities/15', { calculate: true });
         * Returns: '/custom_securities/15?calculate=true'
         */
        if (!queryParams) {
            return urlTemplate;
        }

        const params = Object.keys(queryParams)
            .map((key: string) => `${key}=${encodeURIComponent(queryParams[key])}`)
            .join('&');
        return `${urlTemplate}?${params}`;
    }

    sanitizeUrl(url: string): string {
        // If sanitize returns null, return an empty string
        return this.domSanitizer.sanitize(SecurityContext.URL, url) || '';
    }
}
